import { logger } from './logger';

// Tipos de erro personalizados
export class ValidationError extends Error {
  constructor(message: string, public field?: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

export class AuthenticationError extends Error {
  constructor(message: string = 'Acesso não autorizado') {
    super(message);
    this.name = 'AuthenticationError';
  }
}

export class NotFoundError extends Error {
  constructor(resource: string = 'Recurso') {
    super(`${resource} não encontrado`);
    this.name = 'NotFoundError';
  }
}

export class PermissionError extends Error {
  constructor(message: string = 'Permissão negada') {
    super(message);
    this.name = 'PermissionError';
  }
}

export class RateLimitError extends Error {
  constructor(message: string = 'Muitas tentativas. Tente novamente mais tarde.') {
    super(message);
    this.name = 'RateLimitError';
  }
}

// Interface para resposta de erro padronizada
export interface ErrorResponse {
  success: false;
  error: string;
  code?: string;
  timestamp: string;
}

// Interface para resposta de sucesso padronizada
export interface SuccessResponse<T = any> {
  success: true;
  data: T;
  timestamp: string;
}

// Função para sanitizar mensagens de erro (remove informações sensíveis)
const sanitizeErrorMessage = (error: any): string => {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof ValidationError) {
    return error.message;
  }

  if (error instanceof AuthenticationError) {
    return 'Erro de autenticação';
  }

  if (error instanceof NotFoundError) {
    return error.message;
  }

  if (error instanceof PermissionError) {
    return 'Acesso negado';
  }

  if (error instanceof RateLimitError) {
    return error.message;
  }

  // Erros do Firebase
  if (error?.code) {
    switch (error.code) {
      case 'auth/user-not-found':
        return 'Usuário não encontrado';
      case 'auth/wrong-password':
        return 'Senha incorreta';
      case 'auth/email-already-in-use':
        return 'Email já está em uso';
      case 'auth/weak-password':
        return 'Senha muito fraca';
      case 'auth/invalid-email':
        return 'Email inválido';
      case 'permission-denied':
        return 'Acesso negado';
      case 'not-found':
        return 'Documento não encontrado';
      case 'already-exists':
        return 'Documento já existe';
      case 'resource-exhausted':
        return 'Limite de recursos excedido';
      case 'unauthenticated':
        return 'Usuário não autenticado';
      default:
        return 'Erro interno do servidor';
    }
  }

  // Erros de rede
  if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
    return 'Erro de conexão. Verifique sua internet.';
  }

  // Erro genérico - não expor detalhes internos
  return 'Erro interno do servidor';
};

// Função principal para tratamento de erros
export const handleError = (error: any, context?: string): ErrorResponse => {
  const timestamp = new Date().toISOString();
  const sanitizedMessage = sanitizeErrorMessage(error);
  
  // Log completo do erro (apenas no servidor)
  logger.error('Error handled', {
    context,
    error: {
      name: error?.name,
      message: error?.message,
      stack: error?.stack,
      code: error?.code
    },
    timestamp
  });

  // Resposta sanitizada para o cliente
  return {
    success: false,
    error: sanitizedMessage,
    code: error?.name || 'UNKNOWN_ERROR',
    timestamp
  };
};

// Função para criar resposta de sucesso padronizada
export const createSuccessResponse = <T>(data: T): SuccessResponse<T> => ({
  success: true,
  data,
  timestamp: new Date().toISOString()
});

// Wrapper para funções assíncronas com tratamento de erro
export const withErrorHandling = <T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  context?: string
) => {
  return async (...args: T): Promise<SuccessResponse<R> | ErrorResponse> => {
    try {
      const result = await fn(...args);
      return createSuccessResponse(result);
    } catch (error) {
      return handleError(error, context);
    }
  };
};

// Middleware para validação de entrada
export const validateInput = <T>(
  data: any,
  validator: (data: any) => { isValid: boolean; errors: string[]; sanitizedValue?: T }
): T => {
  const validation = validator(data);
  
  if (!validation.isValid) {
    throw new ValidationError(`Dados inválidos: ${validation.errors.join(', ')}`);
  }
  
  return validation.sanitizedValue!;
};

// Função para verificar permissões
export const checkPermission = (condition: boolean, message?: string): void => {
  if (!condition) {
    throw new PermissionError(message);
  }
};

// Função para verificar autenticação
export const requireAuth = (userId?: string): void => {
  if (!userId) {
    throw new AuthenticationError();
  }
};

// Função para verificar se recurso existe
export const requireResource = (resource: any, name: string = 'Recurso'): void => {
  if (!resource) {
    throw new NotFoundError(name);
  }
};